/**
 * Reading a number the user typed.
 *
 * One rule holds for every numeric field, whatever the browser's or the
 * operating system's regional settings: a point and a comma are both read as
 * the decimal separator, so `1,5` and `1.5` are the same value. That is what
 * a keyboard whose numeric keypad types a comma (fi-FI, de-DE, es-ES, …)
 * produces, and it does not depend on the locale the page runs in.
 *
 * No grouping (thousands) separator is accepted. An entry holding more than
 * one separator, or anything other than an optional leading minus, digits
 * and one separator (`+1,5`, ` 12 `, `1e3`), is not a number: it is reported
 * as such, never read as the number its leading digits spell
 * (`parseFloat('1,5')` is `1`), and never reduced to a number by dropping
 * the characters that do not fit.
 *
 * One entry shape is a number under this rule and also how a thousands
 * separator writes a whole number: `1,500` is 1500 in en-US, `1.500` is 1500
 * in de-DE, and both are 1.5 here. Where a field can take the whole number,
 * the entry is ambiguous ({@link ambiguousDecimalReadings}) and the field
 * asks for the number in a form only one reading fits, offering only the
 * readings it takes; where it cannot (a density of at most 20 is never 1500),
 * the entry is that decimal. The whole-number reading is never taken.
 *
 * Values are displayed with a point (`Number.prototype.toFixed` or
 * {@link decimalEntryText}), which this rule reads back to the same value.
 */

/** The characters read as the decimal separator. */
const DECIMAL_SEPARATORS = /[.,]/g;

/**
 * A complete decimal entry with the separator normalised to a point: an
 * optional minus and digits with an optional fraction (`1`, `1.`, `1.5`,
 * `.5`). The fraction is captured so its digits can be counted.
 */
const COMPLETE_DECIMAL = /^-?(?:\d+(?:\.(\d*))?|\.(\d+))$/;

/**
 * The digits after the separator that carry value: `fraction` without its
 * trailing zeros (`500` → `5`, `000` → none).
 */
function significantFractionDigits(fraction: string): string {
    return fraction.replace(/0+$/, '');
}

/** Text made only of what a decimal entry is written with. */
const DECIMAL_ENTRY_CHARACTERS = /^-?[\d.,]*$/;

/**
 * `text` with its decimal separator written as a point, or `null` when it
 * holds more than one separator, which would make the entry ambiguous
 * (`1.000,5`, `1,000,000`). Characters other than separators are left as they
 * are, so the caller still decides what else the entry may hold.
 */
export function normalizeDecimalSeparator(text: string): string | null {
    const separators = text.match(DECIMAL_SEPARATORS) ?? [];
    if (separators.length > 1) {
        return null;
    }
    return text.replace(',', '.');
}

/**
 * The number a typed decimal entry names, or `null` when the entry is empty,
 * is not a decimal number under the rule above, or has more than
 * `maxFractionDigits` digits after the separator. Trailing zeros are not
 * counted, since they do not change the value: `100.00` fits one decimal as
 * 100.0, and `12.0` fits none as 12. The value is not rounded, clamped or
 * range-checked: that is the field's decision, and it is made on the number
 * the user actually wrote.
 */
export function parseDecimalInput(
    text: string,
    maxFractionDigits: number = Number.POSITIVE_INFINITY
): number | null {
    const normalized = normalizeDecimalSeparator(text);
    const match =
        normalized === null ? null : COMPLETE_DECIMAL.exec(normalized);
    if (match === null) {
        return null;
    }
    const fraction = match[1] ?? match[2] ?? '';
    if (significantFractionDigits(fraction).length > maxFractionDigits) {
        return null;
    }
    const value = Number(normalized);
    return Number.isFinite(value) ? value : null;
}

/**
 * Whether `text` is made only of the characters a decimal entry is written
 * with: a leading minus, digits and separators. A field may hold such text
 * while the user types it, whether or not it is a number yet (`-`, `1,`,
 * `1,5,3`), and shows it as invalid until it is one. Digits and separators
 * are therefore never refused keystroke by keystroke: refusing a separator
 * joins the digits around it (`1,5` → `15`), and refusing a digit shortens
 * the number.
 */
export function isDecimalEntryText(text: string): boolean {
    return DECIMAL_ENTRY_CHARACTERS.test(text);
}

/**
 * An entry written like a whole number with one thousands separator: one to
 * three digits, the first not 0, one separator and exactly three digits
 * (`1,500`, `-12.250`). `0,500` and `1,5000` do not have that shape.
 */
const THOUSANDS_GROUPED = /^(-?)([1-9]\d{0,2})[.,](\d{3})$/;

/**
 * `value` written as a decimal entry this module reads back to the same
 * value: digits with a point where there is a fraction, never an exponent
 * (`String(5e-7)` is `5e-7`, which is no entry; this is `0.0000005`). The
 * digits are those of `String(value)`, the shortest that name the value.
 */
export function decimalEntryText(value: number): string {
    const text = String(value);
    const exponentAt = text.indexOf('e');
    if (exponentAt === -1) {
        return text;
    }
    const sign = text.startsWith('-') ? '-' : '';
    const [mantissa, exponent] = text.slice(sign.length).split('e');
    const [integerPart, fraction = ''] = mantissa.split('.');
    const digits = integerPart + fraction;
    const pointAt = integerPart.length + Number(exponent);
    if (pointAt <= 0) {
        return `${sign}0.${'0'.repeat(-pointAt)}${digits}`;
    }
    if (pointAt >= digits.length) {
        return `${sign}${digits}${'0'.repeat(pointAt - digits.length)}`;
    }
    return `${sign}${digits.slice(0, pointAt)}.${digits.slice(pointAt)}`;
}

/**
 * The two numbers an ambiguous entry can name, and how to type each one the
 * field takes.
 */
export interface AmbiguousDecimalReadings {
    /** The entry read under this module's rule: `1,500` → 1.5. */
    readonly decimal: number;
    /** The entry read as a whole number with a thousands separator: `1,500` → 1500. */
    readonly whole: number;
    /**
     * {@link decimal} typed so that only it fits: `1.5` for `1,500`, `1` for
     * `1.000`, and `1.2340` for `1,234`, whose three decimals would keep the
     * ambiguous shape. `null` when the field does not take {@link decimal}
     * typed that way (out of its range, or more decimals than it holds), so
     * the entry can only have been meant as {@link whole}.
     */
    readonly decimalText: string | null;
    /** {@link whole} typed without a separator: `1500`. */
    readonly wholeText: string;
}

/**
 * The two readings of `text` when it is written like a whole number with a
 * thousands separator (`1,500`, `1.500`) and `accepts` takes the whole
 * number, else null. `accepts` is the field's own test of a value (its range,
 * its scale), and `maxFractionDigits` the most decimals it takes, as for
 * {@link parseDecimalInput}. Where the field refuses the whole number, the
 * entry is not ambiguous for it and reads as the decimal. Where it takes the
 * whole number the entry is ambiguous even if the decimal is no value the
 * field takes (`1,500` in a window width on a whole-unit scale), since the
 * user may well have meant fifteen hundred; the readings then offer no
 * spelling of the decimal (`decimalText` is null).
 */
export function ambiguousDecimalReadings(
    text: string,
    accepts: (value: number) => boolean,
    maxFractionDigits: number = Number.POSITIVE_INFINITY
): AmbiguousDecimalReadings | null {
    const match = THOUSANDS_GROUPED.exec(text);
    if (match === null) {
        return null;
    }
    const [, sign, integerPart, fraction] = match;
    const decimal = Number(`${sign}${integerPart}.${fraction}`);
    const whole = Number(`${sign}${integerPart}${fraction}`);
    if (!accepts(whole)) {
        return null;
    }
    const significantFraction = significantFractionDigits(fraction);
    const decimalText =
        significantFraction === ''
            ? `${sign}${integerPart}`
            : `${sign}${integerPart}.${significantFraction}${significantFraction.length === 3 ? '0' : ''}`;
    const takesDecimal =
        accepts(decimal) &&
        parseDecimalInput(decimalText, maxFractionDigits) !== null;
    return {
        decimal,
        whole,
        decimalText: takesDecimal ? decimalText : null,
        wholeText: `${sign}${integerPart}${fraction}`,
    };
}

/**
 * The message telling the user that the entry `text` with `readings` is not
 * accepted, and how to type each number it can name that the field takes:
 * both (`decimalInput.ambiguous`), or the whole number alone
 * (`decimalInput.wholeOnly`).
 */
export function ambiguousDecimalMessage(
    text: string,
    readings: AmbiguousDecimalReadings
): { id: string; values: Record<string, string> } {
    return readings.decimalText === null
        ? {
              id: 'decimalInput.wholeOnly',
              values: { typed: text, whole: readings.wholeText },
          }
        : {
              id: 'decimalInput.ambiguous',
              values: {
                  typed: text,
                  decimal: readings.decimalText,
                  whole: readings.wholeText,
              },
          };
}
